import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import {
  X,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Shield,
  Star,
  TrendingUp,
  MessageCircle,
  Dumbbell,
  Newspaper,
  BarChart3,
  Loader2,
  RefreshCw,
  Calendar,
  Apple,
  Plus,
  Search,
  Trash2,
  Film,
  Upload,
  CheckCircle,
  AlertCircle,
  Zap,
} from 'lucide-react';
import SlamFitGauge from './SlamFitGauge';
import { CAROLINA_BLUE, ACCENT, RISK_COLORS, getInitials } from '../../config/uncConstants';
import { C } from '../design-system/primitives/constants';

// ═══════════════════════════════════════════════════════════════════════════
// PLAYER CARD TABS
// ═══════════════════════════════════════════════════════════════════════════

const PLAYER_TABS = [
  { id: 'news', label: 'News & Intel', icon: Newspaper },
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'film', label: 'Film', icon: Film },
  { id: 'ask-ai', label: 'Ask SLAM AI', icon: MessageCircle },
  { id: 'workouts', label: 'Workouts', icon: Dumbbell },
  { id: 'nutrition', label: 'Nutrition', icon: Apple },
];

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const MAX_FILM_SIZE = 100 * 1024 * 1024; // 100MB

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function InitialsAvatar({ name, size = 64 }) {
  return (
    <div
      className="flex items-center justify-center rounded-full font-bold uppercase"
      style={{
        width: size,
        height: size,
        background: CAROLINA_BLUE,
        color: '#000',
        fontSize: size * 0.38,
      }}
    >
      {getInitials(name)}
    </div>
  );
}

function TransferRiskPill({ risk }) {
  const r = (risk || 'LOW').toUpperCase();
  const bg = RISK_COLORS[r] || RISK_COLORS.LOW;
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase"
      style={{ background: `${bg}22`, color: bg, border: `1px solid ${bg}44` }}
    >
      {r}
    </span>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-lg font-light text-white">{value ?? '--'}</div>
      <div className="text-[9px] tracking-widest uppercase text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

// Skeleton loading pulse bars
function SkeletonLine({ width = '100%', height = 14 }) {
  return (
    <div
      className="rounded-md animate-pulse"
      style={{ width, height, background: 'rgba(255,255,255,0.06)' }}
    />
  );
}

function SkeletonArticle() {
  return (
    <div className="p-3 rounded-lg bg-black/40 border border-zinc-800 space-y-2">
      <SkeletonLine width="85%" height={14} />
      <SkeletonLine width="60%" height={10} />
      <SkeletonLine width="100%" height={10} />
    </div>
  );
}

// Module-level caches — survive tab switches and re-renders within a session
const _newsCache = new Map();   // playerName → { articles, timestamp }
const _statsCache = new Map();  // playerName → { stats, timestamp }

// ═══════════════════════════════════════════════════════════════════════════
// NEWS & INTEL TAB (loads first, with animation + sticky cache)
// ═══════════════════════════════════════════════════════════════════════════

function NewsTab({ playerName }) {
  const [articles, setArticles] = useState(() => _newsCache.get(playerName)?.articles || []);
  const [loading, setLoading] = useState(!_newsCache.has(playerName));
  const [error, setError] = useState(null);

  const fetchNews = useCallback(async (force = false) => {
    if (!force && _newsCache.has(playerName)) {
      setArticles(_newsCache.get(playerName).articles);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/unc/player-news/${encodeURIComponent(playerName)}`);
      if (res.ok) {
        const d = await res.json();
        const payload = d?.data || d;
        const list = payload?.articles || [];
        setArticles(list);
        _newsCache.set(playerName, { articles: list, timestamp: Date.now() });
      } else {
        setError('Failed to load news');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [playerName]);

  useEffect(() => { fetchNews(); }, [fetchNews]);

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-4">
          <Loader2 size={14} className="animate-spin text-zinc-500" />
          <span className="text-xs text-zinc-500">Searching for latest intel...</span>
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-fadeInUp" style={{ animationDelay: `${i * 80}ms` }}>
            <SkeletonArticle />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-widest uppercase text-zinc-500 flex items-center gap-1.5">
          <Newspaper size={11} /> Latest Intel — {playerName}
        </div>
        <button
          onClick={() => fetchNews(true)}
          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
        >
          <RefreshCw size={10} /> Refresh
        </button>
      </div>

      {error && (
        <div className="text-xs text-sky-400 bg-sky-900/20 px-3 py-2 rounded-lg border border-sky-800/30">
          {error}
        </div>
      )}

      {articles.length === 0 && !error && (
        <div className="text-center py-8 text-zinc-500 text-sm">
          No recent news found for {playerName}.
        </div>
      )}

      {articles.map((a, i) => (
        <a
          key={i}
          href={a.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block p-3 rounded-lg bg-black/40 border border-zinc-800 hover:border-zinc-600 transition-all group animate-fadeInUp"
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <div className="flex items-start gap-2">
            <ExternalLink size={12} className="mt-1 text-zinc-600 group-hover:text-[#7BAFD4] shrink-0 transition-colors" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-zinc-300 group-hover:text-white transition-colors line-clamp-2">
                {a.title}
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] font-medium text-zinc-500">{a.source}</span>
                {a.publishedDate && (
                  <>
                    <span className="text-zinc-700">·</span>
                    <span className="text-[10px] text-zinc-600 flex items-center gap-0.5">
                      <Calendar size={8} />
                      {new Date(a.publishedDate).toLocaleDateString()}
                    </span>
                  </>
                )}
              </div>
              {a.summary && (
                <div className="text-[11px] text-zinc-500 mt-1.5 line-clamp-2 leading-relaxed">
                  {a.summary}
                </div>
              )}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB (existing content — stats, fit score, draft, deep analysis)
// ═══════════════════════════════════════════════════════════════════════════

function DimensionBar({ label, score, color }) {
  const pct = Math.min(Math.max(score || 0, 0), 100);
  return (
    <div>
      <div className="flex justify-between text-[10px] mb-1">
        <span className="text-zinc-500">{label}</span>
        <span className="text-zinc-400 font-mono">{Math.round(pct)}</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function GradeBadgeLarge({ grade }) {
  const GRADE_COLORS = {
    'A+': '#00CC66', 'A': '#00CC66', 'A-': '#22C55E',
    'B+': '#84CC16', 'B': '#F59E0B', 'B-': '#F59E0B',
    'C+': '#F97316', 'C': '#F97316', 'C-': '#EF4444',
    'D': '#EF4444', 'F': '#DC2626',
  };
  const color = GRADE_COLORS[grade] || '#71717a';
  return (
    <div
      className="flex items-center justify-center w-10 h-10 rounded-lg text-sm font-bold"
      style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}
    >
      {grade || '--'}
    </div>
  );
}

function BioItem({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[9px] tracking-widest uppercase text-zinc-600">{label}</div>
      <div className="text-xs text-zinc-300 mt-0.5">{value}</div>
    </div>
  );
}

function OverviewTab({ player, evaluation, onEvaluate }) {
  const [showDeepAnalysis, setShowDeepAnalysis] = useState(false);
  const [fetchedStats, setFetchedStats] = useState(() => _statsCache.get(player.name)?.stats || null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  const ev = evaluation || {};
  const evStats = ev.stats || player.stats || {};
  const hasEvStats = evStats.avg || evStats.AVG || evStats.hr || evStats.HR;
  const stats = hasEvStats ? evStats : (fetchedStats || {});
  const fitScore = ev.slamFitScore ?? ev.slam_fit_score ?? 0;
  const grade = ev.grade || ev.overallGrade || '';
  const breakdown = ev.breakdown || {};
  const strengths = ev.strengths || [];
  const weaknesses = ev.weaknesses || ev.areasForImprovement || [];
  const draftRound = ev.draftProjectedRound ?? ev.draft_projected_round;
  const draftEligibility = ev.draftEligibility ?? ev.draft_eligibility;
  const deepText = ev.deepAnalysis ?? ev.deep_analysis ?? '';
  const hasEval = fitScore > 0;

  // Fetch stats from NatStat BigQuery if evaluation stats are missing
  useEffect(() => {
    if (hasEvStats || fetchedStats) return;
    if (_statsCache.has(player.name)) {
      setFetchedStats(_statsCache.get(player.name).stats);
      return;
    }
    setStatsLoading(true);
    fetch(`/api/unc/player-stats/${encodeURIComponent(player.name)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        const data = d?.data || d;
        if (data?.found && data.stats) {
          setFetchedStats(data.stats);
          _statsCache.set(player.name, { stats: data.stats, timestamp: Date.now() });
        }
      })
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, [player.name, hasEvStats, fetchedStats]);

  async function handleEvaluate() {
    if (!onEvaluate || evaluating) return;
    setEvaluating(true);
    try {
      await onEvaluate(player.name);
    } finally {
      setEvaluating(false);
    }
  }

  // Computed stat highlights
  const gp = stats.gp ?? stats.GP ?? '--';
  const isPitcher = /^(RHP|LHP|P)$/i.test(player.position);

  return (
    <div className="space-y-4">
      {/* ── Player Bio Card ── */}
      <div className="p-4 rounded-xl bg-black/50 border border-zinc-800">
        <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-3">Player Bio</div>
        <div className="grid grid-cols-3 gap-3">
          <BioItem label="Position" value={player.position} />
          <BioItem label="Number" value={player.number ? `#${player.number}` : null} />
          <BioItem label="Class" value={player.class_year || player.year} />
          <BioItem label="Height" value={player.height} />
          <BioItem label="Weight" value={player.weight ? `${player.weight} lbs` : null} />
          <BioItem label="B/T" value={player.bats_throws || player.bat_throw} />
          <BioItem label="Hometown" value={player.hometown} />
          <BioItem label="High School" value={player.high_school} />
          <BioItem label="Games Played" value={gp !== '--' ? gp : null} />
        </div>
        {(player.profile_url || player.social_links) && (
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-zinc-800/50">
            {player.profile_url && (
              <a
                href={player.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] flex items-center gap-1 text-zinc-500 hover:text-white transition-colors"
              >
                <ExternalLink size={10} /> GoHeels Profile
              </a>
            )}
            {player.social_links?.twitter && (
              <a
                href={`https://twitter.com/${player.social_links.twitter.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-zinc-500 hover:text-white transition-colors"
              >
                {player.social_links.twitter}
              </a>
            )}
            {player.social_links?.instagram && (
              <a
                href={`https://instagram.com/${player.social_links.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-zinc-500 hover:text-white transition-colors"
              >
                {player.social_links.instagram}
              </a>
            )}
          </div>
        )}
      </div>

      {/* ── SLAM Fit Score + Grade + Evaluate ── */}
      <div className="flex items-center gap-4 p-4 rounded-xl bg-black/50 border border-zinc-800">
        <SlamFitGauge score={fitScore} size={72} strokeWidth={5} />
        <div className="flex-1">
          <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1">SLAM Fit Score</div>
          <div className="flex items-center gap-3">
            <div className="text-2xl font-light text-white">{fitScore > 0 ? Math.round(fitScore) : '--'}</div>
            {grade && <GradeBadgeLarge grade={grade} />}
          </div>
        </div>
        {onEvaluate && (
          <button
            onClick={handleEvaluate}
            disabled={evaluating}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:brightness-110 disabled:opacity-50 shrink-0"
            style={{ background: ACCENT, color: '#000' }}
          >
            {evaluating ? (
              <><Loader2 size={12} className="animate-spin" /> Evaluating...</>
            ) : (
              <><Zap size={12} /> {hasEval ? 'Re-Evaluate' : 'Evaluate'}</>
            )}
          </button>
        )}
      </div>

      {/* ── Dimension Breakdown ── */}
      {hasEval && (breakdown.statsScore || breakdown.programFitScore) && (
        <div className="p-4 rounded-xl bg-black/40 border border-zinc-800 space-y-2.5">
          <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Score Breakdown</div>
          <DimensionBar label="Stats Performance" score={breakdown.statsScore} color={CAROLINA_BLUE} />
          <DimensionBar label="Program Fit (Scott Forbes System)" score={breakdown.programFitScore} color={ACCENT} />
          <DimensionBar label="Character & Leadership" score={breakdown.characterScore} color="#22C55E" />
          <DimensionBar label="MLB Draft Potential" score={breakdown.mlbPotentialScore} color="#8B5CF6" />
        </div>
      )}

      {/* ── Strengths & Weaknesses ── */}
      {(strengths.length > 0 || weaknesses.length > 0) && (
        <div className="grid grid-cols-2 gap-3">
          {strengths.length > 0 && (
            <div className="p-3 rounded-xl bg-black/40 border border-zinc-800">
              <div className="text-[10px] tracking-widest uppercase text-emerald-500/70 mb-2">Strengths</div>
              <ul className="space-y-1">
                {strengths.slice(0, 5).map((s, i) => (
                  <li key={i} className="text-[11px] text-zinc-400 flex items-start gap-1.5">
                    <span className="text-emerald-500 mt-0.5 shrink-0">+</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {weaknesses.length > 0 && (
            <div className="p-3 rounded-xl bg-black/40 border border-zinc-800">
              <div className="text-[10px] tracking-widest uppercase text-red-500/70 mb-2">Areas to Develop</div>
              <ul className="space-y-1">
                {weaknesses.slice(0, 5).map((w, i) => (
                  <li key={i} className="text-[11px] text-zinc-400 flex items-start gap-1.5">
                    <span className="text-red-400 mt-0.5 shrink-0">-</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Season Stats (full) ── */}
      <div>
        <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-3 flex items-center gap-1.5">
          <TrendingUp size={11} /> {isPitcher ? 'Pitching Stats' : 'Batting Stats'}
          {statsLoading && <Loader2 size={10} className="animate-spin text-zinc-600 ml-1" />}
          <span className="text-[9px] text-zinc-600 ml-auto font-normal normal-case tracking-normal">
            {stats.gp ? `NatStat · ${stats.gp} GP` : 'NatStat NCAA D1'}
          </span>
        </div>
        <div className="grid grid-cols-5 gap-3 p-3 rounded-xl bg-black/40 border border-zinc-800">
          <StatBox label="AVG" value={stats.avg ?? stats.AVG} />
          <StatBox label="OBP" value={stats.obp ?? stats.OBP} />
          <StatBox label="SLG" value={stats.slg ?? stats.SLG} />
          <StatBox label="OPS" value={stats.ops ?? stats.OPS} />
          <StatBox label="HR" value={stats.hr ?? stats.HR} />
        </div>
        <div className="grid grid-cols-5 gap-3 p-3 mt-1.5 rounded-xl bg-black/30 border border-zinc-800/60">
          <StatBox label="H" value={stats.h ?? stats.H} />
          <StatBox label="RBI" value={stats.rbi ?? stats.RBI} />
          <StatBox label="R" value={stats.r ?? stats.R} />
          <StatBox label="BB" value={stats.bb ?? stats.BB} />
          <StatBox label="SO" value={stats.so ?? stats.SO} />
        </div>
        <div className="grid grid-cols-5 gap-3 p-3 mt-1.5 rounded-xl bg-black/30 border border-zinc-800/60">
          <StatBox label="2B" value={stats.doubles} />
          <StatBox label="3B" value={stats.triples} />
          <StatBox label="SB" value={stats.sb ?? stats.SB} />
          <StatBox label="AB" value={stats.ab ?? stats.AB} />
          <StatBox label="HBP" value={stats.hbp ?? stats.HBP} />
        </div>
      </div>

      {/* ── MLB Draft ── */}
      {(draftRound || draftEligibility) && (
        <div className="flex items-center gap-4 p-4 rounded-xl bg-black/40 border border-zinc-800">
          <Shield size={16} style={{ color: ACCENT }} />
          <div>
            <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-0.5">MLB Draft</div>
            <div className="flex items-center gap-3 text-sm text-zinc-300">
              {draftRound && <span>Projected: <strong className="text-white">Round {draftRound}</strong></span>}
              {draftEligibility && <span className="text-zinc-600">|</span>}
              {draftEligibility && <span>{draftEligibility}</span>}
            </div>
          </div>
        </div>
      )}

      {/* ── Deep Analysis (expandable) ── */}
      {deepText && (
        <div className="rounded-xl border border-zinc-800 overflow-hidden">
          <button
            onClick={() => setShowDeepAnalysis((p) => !p)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm text-zinc-400 hover:text-white transition-colors bg-black/40"
          >
            <span className="text-[10px] tracking-widest uppercase">Scouting Report</span>
            {showDeepAnalysis ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showDeepAnalysis && (
            <div className="px-4 py-3 text-sm leading-relaxed text-zinc-400 bg-black/20 border-t border-zinc-800 whitespace-pre-wrap">
              {deepText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ASK SLAM AI TAB (embedded chat with player context)
// ═══════════════════════════════════════════════════════════════════════════

// Thinking dots animation for AI streaming
function ThinkingDots() {
  return (
    <div className="flex items-center gap-1.5 py-1">
      <span className="text-[10px] text-zinc-500 mr-1">Thinking</span>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{
            background: CAROLINA_BLUE,
            animation: 'thinkPulse 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </div>
  );
}

function AskAITab({ player, evaluation }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef = React.useRef(null);

  // Build enriched baseball-specific context from all available data
  const playerContext = useMemo(() => {
    const ev = evaluation || {};
    const evStats = ev.stats || player.stats || {};
    const cached = _statsCache.get(player.name)?.stats || {};
    const stats = (evStats.avg || evStats.AVG) ? evStats : cached;
    const news = _newsCache.get(player.name)?.articles || [];
    const newsContext = news.length > 0
      ? `\nRecent baseball news: ${news.slice(0, 3).map((a) => `"${a.title}" (${a.source})`).join('; ')}.`
      : '';

    // Bio
    const bio = [
      `Player: ${player.name}`,
      `#${player.number || '?'}, ${player.position || 'Unknown'}, ${player.class_year || player.year || ''}`,
      `UNC Tar Heels Baseball (ACC), Head Coach: Scott Forbes`,
      player.height ? `Height: ${player.height}` : '',
      player.weight ? `Weight: ${player.weight} lbs` : '',
      player.hometown ? `From: ${player.hometown}` : '',
      player.high_school ? `HS: ${player.high_school}` : '',
    ].filter(Boolean).join('. ') + '.';

    // Stats
    const statsLine = stats.avg
      ? `\nSeason Stats (NatStat NCAA D1): AVG ${stats.avg}, OBP ${stats.obp ?? 'N/A'}, SLG ${stats.slg ?? 'N/A'}, OPS ${stats.ops ?? 'N/A'}, HR ${stats.hr ?? 0}, RBI ${stats.rbi ?? 0}, R ${stats.r ?? 0}, H ${stats.h ?? 0}, BB ${stats.bb ?? 0}, SO ${stats.so ?? 0}, SB ${stats.sb ?? 0}, 2B ${stats.doubles ?? 0}, 3B ${stats.triples ?? 0}, AB ${stats.ab ?? 0}, HBP ${stats.hbp ?? 0}, GP ${stats.gp ?? 'N/A'}.`
      : '';

    // Evaluation
    const evalLine = ev.slamFitScore
      ? `\nSLAM Evaluation: Fit Score ${ev.slamFitScore}/100 (Grade: ${ev.grade || ev.overallGrade || 'N/A'}). Transfer Risk: ${ev.transferRisk || 'N/A'}.`
        + (ev.breakdown ? ` Breakdown — Stats: ${ev.breakdown.statsScore}, Program Fit: ${ev.breakdown.programFitScore}, Character: ${ev.breakdown.characterScore}, MLB Potential: ${ev.breakdown.mlbPotentialScore}.` : '')
        + (ev.strengths?.length ? ` Strengths: ${ev.strengths.join(', ')}.` : '')
        + (ev.weaknesses?.length ? ` Areas to develop: ${ev.weaknesses.join(', ')}.` : '')
      : '';

    const scoutLine = ev.deepAnalysis ? `\nScouting Report: ${ev.deepAnalysis.substring(0, 800)}` : '';

    return `${bio}${statsLine}${evalLine}${scoutLine}${newsContext}\n\nIMPORTANT: This is a UNC college BASEBALL player. Only discuss baseball. Do not reference basketball, football, or other sports.`;
  }, [player, evaluation]);

  const suggestedPrompts = useMemo(() => {
    const pos = player.position || 'position';
    const isPitcherAI = /^(RHP|LHP|P)$/i.test(pos);
    return [
      `What are ${player.name}'s key strengths as a ${pos} at UNC?`,
      isPitcherAI
        ? `Break down ${player.name}'s pitching repertoire and effectiveness`
        : `Analyze ${player.name}'s offensive approach and plate discipline`,
      `How does ${player.name} fit into Scott Forbes' system at UNC?`,
      `What is ${player.name}'s MLB draft outlook?`,
    ];
  }, [player]);

  async function handleSend(text) {
    const msg = text || input.trim();
    if (!msg || streaming) return;

    const userMsg = { role: 'user', content: msg };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setStreaming(true);

    const assistantMsg = { role: 'assistant', content: '' };
    setMessages((prev) => [...prev, assistantMsg]);

    try {
      const res = await fetch('/api/chat/context/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `[Context: ${playerContext}]\n\n${msg}`,
          history: messages.slice(-10),
          sport: 'baseball',
        }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'content' && data.text) {
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    last.content = data.text;
                  }
                  return [...updated];
                });
              }
            } catch {}
          }
        }
      }
    } catch (err) {
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.role === 'assistant') {
          last.content = 'Sorry, I encountered an error. Please try again.';
        }
        return [...updated];
      });
    } finally {
      setStreaming(false);
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="flex flex-col" style={{ minHeight: 360 }}>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-3" style={{ maxHeight: 320, scrollbarWidth: 'thin' }}>
        {messages.length === 0 && (
          <div className="space-y-2">
            <p className="text-xs text-zinc-500 mb-3">Ask anything about {player.name}:</p>
            {suggestedPrompts.map((p, i) => (
              <button
                key={i}
                onClick={() => handleSend(p)}
                className="w-full text-left px-3 py-2 rounded-lg text-xs text-zinc-400 bg-black/40 border border-zinc-800 hover:border-zinc-600 hover:text-white transition-all"
              >
                {p}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`px-3 py-2 rounded-lg text-sm ${
              m.role === 'user'
                ? 'bg-zinc-800 text-white ml-8'
                : 'bg-black/40 border border-zinc-800 text-zinc-300 mr-4'
            }`}
          >
            <div className="whitespace-pre-wrap leading-relaxed">
              {m.content || (streaming && i === messages.length - 1 ? <ThinkingDots /> : '')}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder={`Ask about ${player.name}...`}
          className="flex-1 px-3 py-2 rounded-lg text-sm bg-black/50 border border-zinc-700 text-white placeholder-zinc-600 outline-none focus:border-zinc-500 transition-colors"
          disabled={streaming}
        />
        <button
          onClick={() => handleSend()}
          disabled={!input.trim() || streaming}
          className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30"
          style={{ background: ACCENT, color: '#000' }}
        >
          {streaming ? <Loader2 size={14} className="animate-spin" /> : '→'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// WORKOUTS TAB (player-specific workout generation)
// ═══════════════════════════════════════════════════════════════════════════

function WorkoutsTab({ player }) {
  const [generating, setGenerating] = useState(false);
  const [workout, setWorkout] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);

  const [config, setConfig] = useState({
    fitnessLevel: 'advanced',
    numberOfDays: 4,
    numberOfWeeks: 4,
    focusAreas: ['strength', 'sport specific'],
  });

  async function generateWorkout() {
    setGenerating(true);
    try {
      const res = await fetch('/api/strength/generate-workout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...config,
          numberOfCycles: 1,
          injuries: '',
          trainingLocation: 'UNC Weight Room',
          prescribedExercises: `Baseball-specific program for ${player.name} (${player.position}). Focus on rotational power, arm care, and sport-specific conditioning for a ${player.position}.`,
        }),
      });

      if (res.ok) {
        const d = await res.json();
        setWorkout(d?.data || d);
      }
    } catch (err) {
      console.error('Workout generation error:', err);
    } finally {
      setGenerating(false);
    }
  }

  const focusOptions = ['Strength', 'Conditioning', 'Mobility', 'Sport Specific'];

  return (
    <div className="space-y-4">
      {!workout ? (
        <>
          <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-2">
            Generate a program for {player.name} ({player.position})
          </div>

          {/* Fitness Level */}
          <div>
            <div className="text-[10px] text-zinc-500 mb-2">Fitness Level</div>
            <div className="grid grid-cols-4 gap-1.5">
              {['beginner', 'intermediate', 'advanced', 'elite'].map((l) => (
                <button
                  key={l}
                  onClick={() => setConfig((c) => ({ ...c, fitnessLevel: l }))}
                  className={`px-2 py-1.5 rounded-lg text-[11px] font-medium capitalize transition-all ${
                    config.fitnessLevel === l
                      ? 'text-black'
                      : 'text-zinc-400 bg-black/40 border border-zinc-800 hover:border-zinc-600'
                  }`}
                  style={config.fitnessLevel === l ? { background: ACCENT } : undefined}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Days/Week */}
          <div>
            <div className="text-[10px] text-zinc-500 mb-2">Days / Week</div>
            <div className="grid grid-cols-4 gap-1.5">
              {[3, 4, 5, 6].map((d) => (
                <button
                  key={d}
                  onClick={() => setConfig((c) => ({ ...c, numberOfDays: d }))}
                  className={`px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                    config.numberOfDays === d
                      ? 'text-black'
                      : 'text-zinc-400 bg-black/40 border border-zinc-800 hover:border-zinc-600'
                  }`}
                  style={config.numberOfDays === d ? { background: ACCENT } : undefined}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Focus Areas */}
          <div>
            <div className="text-[10px] text-zinc-500 mb-2">Focus Areas</div>
            <div className="flex flex-wrap gap-1.5">
              {focusOptions.map((f) => {
                const val = f.toLowerCase();
                const active = config.focusAreas.includes(val);
                return (
                  <button
                    key={f}
                    onClick={() => setConfig((c) => ({
                      ...c,
                      focusAreas: active
                        ? c.focusAreas.filter((x) => x !== val)
                        : [...c.focusAreas, val],
                    }))}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                      active
                        ? 'text-black'
                        : 'text-zinc-400 bg-black/40 border border-zinc-800 hover:border-zinc-600'
                    }`}
                    style={active ? { background: ACCENT } : undefined}
                  >
                    {active && '✓ '}{f}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateWorkout}
            disabled={generating}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all hover:brightness-110 disabled:opacity-50"
            style={{ background: ACCENT, color: '#000' }}
          >
            {generating ? (
              <><Loader2 size={14} className="animate-spin" /> Generating...</>
            ) : (
              <><Dumbbell size={14} /> Generate Program</>
            )}
          </button>
        </>
      ) : (
        <>
          {/* Workout result */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-white">{workout.title || 'Custom Program'}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">{workout.summary?.substring(0, 100) || `${config.numberOfWeeks}-week program`}</div>
            </div>
            <button
              onClick={() => setWorkout(null)}
              className="text-[10px] text-zinc-500 hover:text-white transition-colors"
            >
              New Program
            </button>
          </div>

          {/* Days */}
          <div className="space-y-2">
            {(workout.days || workout.weeks?.[0]?.days || []).map((day, i) => (
              <div key={i} className="rounded-lg border border-zinc-800 overflow-hidden">
                <button
                  onClick={() => setExpandedDay(expandedDay === i ? null : i)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-left bg-black/40 hover:bg-black/60 transition-colors"
                >
                  <span className="text-xs text-white font-medium">
                    Day {day.dayNumber || i + 1}: {day.title || day.dayName || 'Training'}
                  </span>
                  {expandedDay === i ? <ChevronUp size={12} className="text-zinc-500" /> : <ChevronDown size={12} className="text-zinc-500" />}
                </button>
                {expandedDay === i && (
                  <div className="px-3 py-2 space-y-1.5 bg-black/20 border-t border-zinc-800">
                    {(day.exercises || day.workout || []).map((ex, j) => (
                      <div key={j} className="flex items-center justify-between text-xs py-1">
                        <span className="text-zinc-300">{ex.name || ex.exercise}</span>
                        <span className="text-zinc-500 font-mono text-[10px]">
                          {ex.sets}×{ex.reps} {ex.rest && `(${ex.rest})`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NUTRITION TAB (USDA FoodData Central API)
// ═══════════════════════════════════════════════════════════════════════════

const USDA_API_KEY = 'DEMO_KEY'; // Free tier — 1000 req/hr
const USDA_BASE = 'https://api.nal.usda.gov/fdc/v1';

function NutritionTab({ player }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [meals, setMeals] = useState([]);
  const [waterGlasses, setWaterGlasses] = useState(0);

  // Daily macro targets (college baseball athlete defaults)
  const targets = { calories: 3000, protein: 200, carbs: 350, fats: 90 };

  const totals = useMemo(() => {
    const t = { calories: 0, protein: 0, carbs: 0, fats: 0 };
    meals.forEach((m) => {
      t.calories += m.calories || 0;
      t.protein += m.protein || 0;
      t.carbs += m.carbs || 0;
      t.fats += m.fats || 0;
    });
    return t;
  }, [meals]);

  async function searchFood() {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `${USDA_BASE}/foods/search?api_key=${USDA_API_KEY}&query=${encodeURIComponent(searchQuery)}&pageSize=8&dataType=Survey (FNDDS),Foundation`
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults((data.foods || []).map((f) => {
          const nutrients = {};
          (f.foodNutrients || []).forEach((n) => {
            if (n.nutrientName === 'Energy') nutrients.calories = Math.round(n.value || 0);
            if (n.nutrientName === 'Protein') nutrients.protein = Math.round(n.value || 0);
            if (n.nutrientName === 'Carbohydrate, by difference') nutrients.carbs = Math.round(n.value || 0);
            if (n.nutrientName === 'Total lipid (fat)') nutrients.fats = Math.round(n.value || 0);
          });
          return {
            fdcId: f.fdcId,
            name: f.description,
            brand: f.brandName || f.brandOwner || '',
            ...nutrients,
          };
        }));
      }
    } catch (err) {
      console.error('USDA search error:', err);
    } finally {
      setSearching(false);
    }
  }

  function addMeal(food) {
    setMeals((prev) => [...prev, { ...food, id: Date.now(), time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    setSearchResults([]);
    setSearchQuery('');
  }

  function removeMeal(id) {
    setMeals((prev) => prev.filter((m) => m.id !== id));
  }

  function MacroBar({ label, current, target, color }) {
    const pct = Math.min((current / target) * 100, 100);
    return (
      <div>
        <div className="flex justify-between text-[10px] mb-1">
          <span className="text-zinc-500">{label}</span>
          <span className="text-zinc-400 font-mono">{current}g / {target}g</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Daily Summary */}
      <div className="p-4 rounded-xl bg-black/50 border border-zinc-800">
        <div className="flex justify-between items-baseline mb-3">
          <div className="text-[10px] tracking-widest uppercase text-zinc-500">Daily Summary — {player.name}</div>
          <div className="text-xs text-zinc-400 font-mono">{totals.calories} / {targets.calories} cal</div>
        </div>
        <div className="space-y-2.5">
          <MacroBar label="Protein" current={totals.protein} target={targets.protein} color="#22C55E" />
          <MacroBar label="Carbs" current={totals.carbs} target={targets.carbs} color={ACCENT} />
          <MacroBar label="Fats" current={totals.fats} target={targets.fats} color="#3B82F6" />
        </div>
      </div>

      {/* Water Intake */}
      <div className="p-3 rounded-xl bg-black/40 border border-zinc-800">
        <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Water Intake</div>
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((g) => (
            <button
              key={g}
              onClick={() => setWaterGlasses(g === waterGlasses ? g - 1 : g)}
              className={`w-7 h-7 rounded-full text-xs flex items-center justify-center transition-all ${
                g <= waterGlasses ? 'text-black' : 'text-zinc-600 bg-black/40 border border-zinc-700'
              }`}
              style={g <= waterGlasses ? { background: '#3B82F6' } : undefined}
            >
              💧
            </button>
          ))}
        </div>
        <div className="text-[10px] text-zinc-600 mt-1">{waterGlasses} / 8 glasses (64 oz)</div>
      </div>

      {/* Food Search */}
      <div>
        <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Log Food (USDA Database)</div>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchFood()}
              placeholder="Search food (e.g., chicken breast, rice)..."
              className="w-full pl-7 pr-3 py-2 rounded-lg text-xs bg-black/50 border border-zinc-700 text-white placeholder-zinc-600 outline-none focus:border-zinc-500"
            />
          </div>
          <button
            onClick={searchFood}
            disabled={searching}
            className="px-3 py-2 rounded-lg text-xs font-medium transition-all"
            style={{ background: ACCENT, color: '#000' }}
          >
            {searching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-2 space-y-1 max-h-48 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {searchResults.map((f) => (
              <button
                key={f.fdcId}
                onClick={() => addMeal(f)}
                className="w-full text-left p-2 rounded-lg bg-black/40 border border-zinc-800 hover:border-zinc-600 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs text-zinc-300 group-hover:text-white truncate flex-1">{f.name}</div>
                  <Plus size={12} className="text-zinc-600 group-hover:text-white shrink-0 ml-2" />
                </div>
                <div className="flex gap-3 mt-1 text-[10px] text-zinc-600">
                  <span>{f.calories || 0} cal</span>
                  <span>P: {f.protein || 0}g</span>
                  <span>C: {f.carbs || 0}g</span>
                  <span>F: {f.fats || 0}g</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Logged Meals */}
      {meals.length > 0 && (
        <div>
          <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Today's Meals</div>
          <div className="space-y-1.5">
            {meals.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-2 rounded-lg bg-black/40 border border-zinc-800">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-zinc-300 truncate">{m.name}</div>
                  <div className="flex gap-3 text-[10px] text-zinc-600 mt-0.5">
                    <span>{m.time}</span>
                    <span>{m.calories} cal</span>
                    <span>P:{m.protein}g</span>
                    <span>C:{m.carbs}g</span>
                    <span>F:{m.fats}g</span>
                  </div>
                </div>
                <button
                  onClick={() => removeMeal(m.id)}
                  className="p-1 text-zinc-600 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FILM TAB — Per-Player Video Drop Zone + Analysis History
// ═══════════════════════════════════════════════════════════════════════════

function FilmTab({ player }) {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [analysisState, setAnalysisState] = useState('idle'); // idle | uploading | analyzing | done | error
  const [analyses, setAnalyses] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const fileInputRef = useRef(null);

  const playerId = player?.id || player?.playerId || player?.name;
  const playerName = player?.name || 'Unknown';

  // Load analysis history for this player
  useEffect(() => {
    if (!playerId) return;
    setLoadingHistory(true);
    fetch(`/api/player-cards/${encodeURIComponent(playerId)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const d = data?.data || data;
        if (d?.supplemental_data) {
          try {
            const supp = typeof d.supplemental_data === 'string'
              ? JSON.parse(d.supplemental_data) : d.supplemental_data;
            setAnalyses(supp.individual_analyses || supp.analyses || []);
          } catch { setAnalyses([]); }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [playerId]);

  const addFiles = useCallback((newFiles) => {
    const validated = Array.from(newFiles).filter(
      (f) => ACCEPTED_VIDEO_TYPES.includes(f.type) && f.size <= MAX_FILM_SIZE
    ).slice(0, 5);
    setFiles((prev) => [...prev, ...validated]);
  }, []);

  const analyzeFilm = useCallback(async () => {
    if (files.length === 0 || !playerId) return;
    setAnalysisState('uploading');
    try {
      const formData = new FormData();
      formData.append('playerId', playerId);
      formData.append('playerName', playerName);
      formData.append('sport', 'baseball');
      files.forEach((f) => formData.append('media', f));

      setAnalysisState('analyzing');
      const res = await fetch('/api/player/analyze-media-bundle', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error(`Analysis failed: ${res.status}`);
      const result = await res.json();
      const d = result?.data || result;

      // Add to analyses list
      setAnalyses((prev) => [{
        timestamp: new Date().toISOString(),
        type: 'video',
        files: files.map(f => f.name),
        summary: d?.synthesis?.substring(0, 200) || 'Analysis complete',
        ...d,
      }, ...prev]);

      setAnalysisState('done');
      setFiles([]);
    } catch (err) {
      console.error('[FilmTab] Analysis error:', err);
      setAnalysisState('error');
    }
  }, [files, playerId, playerName]);

  return (
    <div className="space-y-4 animate-fadeInUp">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          addFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
        className="rounded-xl p-5 text-center cursor-pointer transition-all duration-200"
        style={{
          background: isDragging ? ACCENT + '15' : 'rgba(255,255,255,0.02)',
          border: `2px dashed ${isDragging ? ACCENT : 'rgba(255,255,255,0.08)'}`,
        }}
      >
        <Upload size={22} className="mx-auto mb-2" style={{ color: isDragging ? ACCENT : 'rgba(255,255,255,0.25)' }} />
        <p className="text-xs font-medium text-zinc-300">
          Drop practice or game film for {playerName}
        </p>
        <p className="text-[10px] text-zinc-500 mt-0.5">
          MP4, MOV, WebM — up to 100MB
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          multiple
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          className="hidden"
        />
      </div>

      {/* Queued files */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-zinc-800/50">
              <div className="flex items-center gap-2">
                <Film size={12} className="text-zinc-500" />
                <span className="text-[11px] text-zinc-300 truncate max-w-[200px]">{f.name}</span>
                <span className="text-[9px] text-zinc-600">{(f.size / (1024*1024)).toFixed(1)}MB</span>
              </div>
              <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>
                <Trash2 size={10} className="text-zinc-600 hover:text-red-400 transition-colors" />
              </button>
            </div>
          ))}

          {/* Analysis status */}
          {analysisState === 'analyzing' && (
            <div className="flex items-center gap-2 py-2 justify-center">
              <Loader2 size={14} className="animate-spin" style={{ color: ACCENT }} />
              <span className="text-[11px] text-zinc-400">Analyzing film for {playerName}...</span>
            </div>
          )}
          {analysisState === 'done' && (
            <div className="flex items-center gap-2 py-2 justify-center">
              <CheckCircle size={14} style={{ color: '#22C55E' }} />
              <span className="text-[11px]" style={{ color: '#22C55E' }}>Analysis stored to player profile</span>
            </div>
          )}
          {analysisState === 'error' && (
            <div className="flex items-center gap-2 py-2 justify-center">
              <AlertCircle size={14} style={{ color: '#EF4444' }} />
              <span className="text-[11px]" style={{ color: '#EF4444' }}>Analysis failed — try a shorter clip</span>
            </div>
          )}

          <button
            onClick={analyzeFilm}
            disabled={analysisState === 'analyzing' || analysisState === 'uploading'}
            className="w-full py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
            style={{ background: ACCENT, color: '#000' }}
          >
            {analysisState === 'analyzing' ? 'Analyzing...' : `Analyze ${files.length} File${files.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {/* Analysis History */}
      <div>
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">
          Analysis History
        </h4>
        {loadingHistory ? (
          <div className="flex justify-center py-4">
            <Loader2 size={14} className="animate-spin text-zinc-600" />
          </div>
        ) : analyses.length === 0 ? (
          <p className="text-[11px] text-zinc-600 text-center py-4">
            No film analyses yet. Drop a video above to get started.
          </p>
        ) : (
          <div className="space-y-2">
            {analyses.slice(0, 10).map((a, i) => (
              <div
                key={i}
                className="px-3 py-2 rounded-lg bg-zinc-800/50 border border-zinc-800"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-medium text-zinc-300">
                    {a.type === 'video' ? 'Video Analysis' : a.type || 'Analysis'}
                  </span>
                  <span className="text-[9px] text-zinc-600">
                    {a.timestamp ? new Date(a.timestamp).toLocaleDateString() : ''}
                  </span>
                </div>
                {a.summary && (
                  <p className="text-[10px] text-zinc-500 line-clamp-2">{a.summary}</p>
                )}
                {a.files && (
                  <div className="flex gap-1 mt-1">
                    {a.files.map((f, j) => (
                      <span key={j} className="text-[8px] px-1 py-0.5 rounded bg-zinc-800 text-zinc-500">{f}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UNCPlayerCard (Tabbed Mini-Dashboard Modal)
// ═══════════════════════════════════════════════════════════════════════════

export default function UNCPlayerCard({ player, evaluation, onClose, isOpen, onEvaluate }) {
  const [tab, setTab] = useState('news'); // News loads first
  const [imgError, setImgError] = useState(false);

  const ev = evaluation || {};
  const transferRisk = ev.transferRisk ?? ev.transfer_risk ?? 'LOW';

  // Close on Escape
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
      setTab('news'); // Always start on News tab
      setImgError(false);
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKey]);

  if (!isOpen || !player) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal — wider for dashboard layout */}
      <div
        className="relative w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl border border-zinc-800 bg-zinc-900 shadow-2xl"
        style={{ scrollbarWidth: 'thin' }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>

        {/* ── Header ── */}
        <div
          className="shrink-0 flex items-center gap-4 p-5 rounded-t-2xl"
          style={{ background: `linear-gradient(135deg, ${CAROLINA_BLUE}15, transparent)` }}
        >
          {player.headshot_url && !imgError ? (
            <img
              src={player.headshot_url}
              alt={player.name}
              className="w-14 h-14 rounded-full object-cover border-2"
              style={{ borderColor: CAROLINA_BLUE }}
              onError={() => setImgError(true)}
            />
          ) : (
            <InitialsAvatar name={player.name} size={56} />
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-white truncate">{player.name}</h2>
            <div className="flex items-center gap-2 mt-0.5 text-sm text-zinc-400">
              {player.number && <span style={{ color: CAROLINA_BLUE }}>#{player.number}</span>}
              {player.position && <span>{player.position}</span>}
              {player.year && <span className="text-zinc-600">|</span>}
              {player.year && <span>{player.year}</span>}
            </div>
          </div>
          <TransferRiskPill risk={transferRisk} />
        </div>

        {/* ── Tab bar ── */}
        <div className="shrink-0 flex gap-1 px-5 py-2 border-b border-zinc-800" style={{ scrollbarWidth: 'none' }}>
          {PLAYER_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all duration-200 ${
                tab === id
                  ? 'text-black'
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
              }`}
              style={tab === id ? { background: ACCENT } : undefined}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin' }}>
          {tab === 'news' && <NewsTab playerName={player.name} />}
          {tab === 'overview' && <OverviewTab player={player} evaluation={evaluation} onEvaluate={onEvaluate} />}
          {tab === 'film' && <FilmTab player={player} />}
          {tab === 'ask-ai' && <AskAITab player={player} evaluation={evaluation} />}
          {tab === 'workouts' && <WorkoutsTab player={player} />}
          {tab === 'nutrition' && <NutritionTab player={player} />}
        </div>
      </div>

      {/* CSS animation keyframe */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.3s ease-out both;
        }
        @keyframes thinkPulse {
          0%, 60%, 100% { opacity: 0.2; transform: scale(0.8); }
          30% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}
